import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  alpha,
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import HistoryIcon from '@mui/icons-material/History';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

interface HelpDialogProps {
  open: boolean;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const HelpDialog: React.FC<HelpDialogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            User Guide
          </Typography>
        </Box>
        <Button
          onClick={onClose}
          sx={{
            color: 'white',
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: alpha('#ffffff', 0.1),
            },
          }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', height: 'calc(90vh - 120px)' }}>
          {/* Tabs */}
          <Box
            sx={{
              borderRight: 1,
              borderColor: 'divider',
              width: 200,
              backgroundColor: alpha(theme.palette.primary.main, 0.02),
            }}
          >
            <Tabs
              orientation="vertical"
              value={value}
              onChange={handleChange}
              sx={{
                borderRight: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textAlign: 'left',
                  alignItems: 'flex-start',
                  minHeight: 48,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                },
              }}
            >
              <Tab label="Getting Started" icon={<CheckCircleIcon />} iconPosition="start" />
              <Tab label="Configuration" icon={<SettingsIcon />} iconPosition="start" />
              <Tab label="Upload Dataset" icon={<CloudUploadIcon />} iconPosition="start" />
              <Tab label="Run Optimization" icon={<PlayArrowIcon />} iconPosition="start" />
              <Tab label="View Results" icon={<TableChartIcon />} iconPosition="start" />
              <Tab label="Charts" icon={<BarChartIcon />} iconPosition="start" />
              <Tab label="Export Data" icon={<FileDownloadIcon />} iconPosition="start" />
              <Tab label="History" icon={<HistoryIcon />} iconPosition="start" />
              <Tab label="Complete Run" icon={<AutoAwesomeIcon />} iconPosition="start" />
            </Tabs>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            {/* Getting Started */}
            <TabPanel value={value} index={0}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                Getting Started
              </Typography>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Accessing the System</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" paragraph>
                    1. Open your web browser and navigate to the system URL
                  </Typography>
                  <Typography variant="body1" paragraph>
                    2. You will see the Landing Page with information about the research project
                  </Typography>
                  <Typography variant="body1" paragraph>
                    3. Click the <strong>"Access the System"</strong> button to proceed to login
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Logging In</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Enter your username and password"
                        secondary="Credentials are provided by your administrator"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Click Login or press Enter"
                        secondary="You'll be redirected to the main application"
                      />
                    </ListItem>
                  </List>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Interface Overview</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Header Bar (Top)
                      </Typography>
                      <Typography variant="body2">
                        Contains export buttons, action buttons (Start/Cancel), and logout option
                      </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.secondary.main, 0.05) }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Left Sidebar
                      </Typography>
                      <Typography variant="body2">
                        Parameter configuration, dataset upload, and optimization control panels
                      </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.success.main, 0.05) }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        Main Content Area
                      </Typography>
                      <Typography variant="body2">
                        Results, charts, progress indicators, and history
                      </Typography>
                    </Paper>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Supported Algorithms</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" paragraph>
                    The system supports eight optimization algorithms:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="GA (Genetic Algorithm)"
                        secondary="Evolutionary optimization"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Cultural Algorithm"
                        secondary="Population-based with cultural knowledge"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="SFLA (Shuffled Frog Leaping Algorithm)"
                        secondary="Memeplex-based optimization"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="SFLAGA"
                        secondary="Hybrid of SFLA and GA"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="SFLACA"
                        secondary="Hybrid of SFLA and Cultural Algorithm"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="PSO (Particle Swarm Optimization)"
                        secondary="Swarm intelligence"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="GAPSO"
                        secondary="Hybrid of GA and PSO"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Kurd SFLA"
                        secondary="Variant of SFLA with enhanced local search"
                      />
                    </ListItem>
                  </List>
                </AccordionDetails>
              </Accordion>
            </TabPanel>

            {/* Configuration */}
            <TabPanel value={value} index={1}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                Configuring Parameters
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Common Parameters
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Task Count"
                      secondary="Number of tasks in your workflow (Range: 1-1000, Default: 10)"
                    />
                    <Chip label="Required" size="small" color="primary" />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="QoS Properties"
                      secondary="Number of quality attributes (Usually 9)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Iterations"
                      secondary="Number of optimization iterations (Auto-optimized based on task count)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Initial Population"
                      secondary="Size of the initial population (Auto-optimized)"
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Algorithm-Specific Parameters
                </Typography>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">Genetic Algorithm (GA)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="Selection Rate"
                          secondary="Percentage of population selected (Default: 10%)"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Crossover Rate"
                          secondary="Probability of crossover (Auto-optimized)"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Mutation Rate"
                          secondary="Probability of mutation (Auto-optimized)"
                        />
                      </ListItem>
                    </List>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">SFLA Parameters</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2">
                      <strong>Memeplex Count:</strong> Number of memeplexes (subgroups) in SFLA algorithm. Auto-optimized based on task count.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Box>

              <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.1), mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  💡 Auto-Optimization Feature
                </Typography>
                <Typography variant="body2">
                  The system automatically suggests optimal parameters based on your task count. These are research-based recommendations that balance performance and execution time.
                </Typography>
              </Paper>
            </TabPanel>

            {/* Upload Dataset */}
            <TabPanel value={value} index={2}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                Uploading Datasets
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Required File Format
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                  <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace' }}>
                    <strong>Format:</strong> CSV or TXT<br />
                    <strong>Required Columns:</strong><br />
                    • ResponseTime<br />
                    • Availability<br />
                    • Throughput<br />
                    • Successability<br />
                    • Reliability<br />
                    • Compliance<br />
                    • BestPractices<br />
                    • Latency<br />
                    • Documentation<br />
                    • ServiceName (optional)<br />
                    • WSDL_URL (optional)
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Upload Steps
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Step 1: Locate Dataset Upload Panel"
                      secondary="Found in the left sidebar"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Step 2: Click 'Choose File' or drag and drop"
                      secondary="Select your dataset file"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Step 3: Wait for validation"
                      secondary="System checks file format and required columns"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Step 4: Confirm upload"
                      secondary="File is loaded into memory"
                    />
                  </ListItem>
                </List>
              </Box>

              <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.warning.main, 0.1), mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  ⚠️ Common Upload Issues
                </Typography>
                <Typography variant="body2" component="div">
                  • <strong>Invalid format:</strong> Ensure file is CSV or TXT<br />
                  • <strong>Missing columns:</strong> Verify all 9 QoS columns are present<br />
                  • <strong>Invalid data:</strong> Check for non-numeric values in QoS columns
                </Typography>
              </Paper>
            </TabPanel>

            {/* Run Optimization */}
            <TabPanel value={value} index={3}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                Running Optimizations
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Before Starting
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Configured all parameters" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Uploaded a valid dataset" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Reviewed parameter settings" />
                  </ListItem>
                </List>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Starting Optimization
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.success.main, 0.1) }}>
                  <Typography variant="body1" paragraph>
                    1. Review your configuration in the Optimization Control panel
                  </Typography>
                  <Typography variant="body1" paragraph>
                    2. Click <strong>"Start Optimization"</strong> button (green button in header)
                  </Typography>
                  <Typography variant="body1" paragraph>
                    3. Monitor progress in the Optimization Progress section
                  </Typography>
                  <Typography variant="body1" paragraph>
                    4. Wait for completion - all 8 algorithms will run sequentially
                  </Typography>
                  <Typography variant="body1">
                    5. View results automatically displayed when complete
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  During Optimization
                </Typography>
                <Typography variant="body2" paragraph>
                  You'll see real-time updates showing:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Current Algorithm"
                      secondary="Which algorithm is currently running (GA, Cultural, SFLA, etc.)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Iteration Counter"
                      secondary="Current generation/iteration number"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Progress Bar"
                      secondary="Visual progress indicator (0-100%)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Run Counter"
                      secondary="Current run number (if running multiple runs)"
                    />
                  </ListItem>
                </List>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Multiple Runs
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.1) }}>
                  <Typography variant="body2" paragraph>
                    To get statistical analysis:
                  </Typography>
                  <Typography variant="body2" component="div">
                    1. Set <strong>"Number of Runs"</strong> in Parameter Panel (e.g., 5 or 10)<br />
                    2. Start Optimization as usual<br />
                    3. System will run optimization multiple times<br />
                    4. <strong>Statistics Tab</strong> will appear with mean, std dev, min, max, median
                  </Typography>
                </Paper>
              </Box>
            </TabPanel>

            {/* View Results */}
            <TabPanel value={value} index={4}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                Viewing Results
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Results Tabs
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Solutions"
                      secondary="Service selection vectors for each algorithm"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Fitness"
                      secondary="Fitness scores (higher is better) - Best values flash green"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Configuration"
                      secondary="Parameters used for this run"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Energy"
                      secondary="Energy consumption in Joules (lower is better) - Best flashes green"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Execution Cost"
                      secondary="Computational cost (lower is better)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Response Time"
                      secondary="Response time in milliseconds (lower is better)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Analysis"
                      secondary="Comprehensive text analysis with colorful, flashing highlights"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Statistics"
                      secondary="Statistical analysis (only if multiple runs were performed)"
                    />
                  </ListItem>
                </List>
              </Box>

              <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.success.main, 0.1), mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  ✨ Visual Highlights
                </Typography>
                <Typography variant="body2">
                  The system automatically highlights <strong style={{ color: theme.palette.success.main }}>best values in green</strong> and <strong style={{ color: theme.palette.error.main }}>worst values in red</strong> with flashing animations to draw your attention.
                </Typography>
              </Paper>
            </TabPanel>

            {/* Charts */}
            <TabPanel value={value} index={5}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                Understanding Charts
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Available Charts
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Fitness Comparison"
                      secondary="Bar chart comparing final fitness scores"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Convergence Analysis"
                      secondary="Line charts showing how fitness improves over iterations"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Similarity Chart"
                      secondary="Performance relative to SFLAGA algorithm"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Energy Consumption"
                      secondary="Energy usage comparison"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Execution Cost"
                      secondary="Cost comparison across algorithms"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Response Time"
                      secondary="Speed performance comparison"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Normalized Performance"
                      secondary="All metrics normalized to 0-1 scale for fair comparison"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Multi-Metric Summary"
                      secondary="Holistic view of all key metrics"
                    />
                  </ListItem>
                </List>
              </Box>

              <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.1), mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  💡 Chart Interaction
                </Typography>
                <Typography variant="body2">
                  Hover over chart elements to see exact values. Use the legend to show/hide specific algorithms.
                </Typography>
              </Paper>
            </TabPanel>

            {/* Export Data */}
            <TabPanel value={value} index={6}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                Exporting Data
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Export Options
                </Typography>
                <Paper sx={{ p: 2, mb: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Export to Excel
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Exports all algorithm results including fitness, energy, cost, response time, solutions, and configuration.
                  </Typography>
                  <Typography variant="body2">
                    <strong>Location:</strong> Header bar → "Export Excel" button
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2, mb: 2, backgroundColor: alpha(theme.palette.secondary.main, 0.05) }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Export Analysis Report
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Exports text-based analysis with winner identifications, rankings, and configuration summary.
                  </Typography>
                  <Typography variant="body2">
                    <strong>Location:</strong> Header bar → "Export Analysis" button
                  </Typography>
                </Paper>
              </Box>

              <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.1), mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  📥 Export Tips
                </Typography>
                <Typography variant="body2">
                  • Files download automatically to your default download folder<br />
                  • Filenames include timestamps for easy organization<br />
                  • Export before clearing history to preserve important results
                </Typography>
              </Paper>
            </TabPanel>

            {/* History */}
            <TabPanel value={value} index={7}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                History Management
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Viewing History
                </Typography>
                <Typography variant="body2" paragraph>
                  The History section shows all past optimization runs with:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Timestamp"
                      secondary="When the run was performed"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Tasks"
                      secondary="Number of tasks used"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Population"
                      secondary="Population size used"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Runs"
                      secondary="Number of runs (if multiple)"
                    />
                  </ListItem>
                </List>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  History Actions
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <TableChartIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="View Details"
                      secondary="Click on a row to see full results and statistics"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <BarChartIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Compare Results"
                      secondary="Select multiple entries and click 'Compare' button"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <FileDownloadIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Export Comparison"
                      secondary="Export comparison results to Excel"
                    />
                  </ListItem>
                </List>
              </Box>

              <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.warning.main, 0.1), mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  ⚠️ Important
                </Typography>
                <Typography variant="body2">
                  Clearing all history cannot be undone. Make sure to export important results before clearing.
                </Typography>
              </Paper>
            </TabPanel>

            {/* Complete Run */}
            <TabPanel value={value} index={8}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: theme.palette.primary.main }}>
                Complete Run Feature
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  What is Complete Run?
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.1) }}>
                  <Typography variant="body2" paragraph>
                    Complete Run executes optimization for multiple task counts automatically. This is useful for:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Performance analysis across different workflow sizes" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Research on scalability" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Finding optimal task count ranges" />
                    </ListItem>
                  </List>
                </Paper>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  How to Use
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Step 1: Configure base parameters"
                      secondary="Set your desired parameters in the Parameter Panel"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Step 2: Upload dataset"
                      secondary="Upload your QWS dataset file"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Step 3: Click 'Complete Run'"
                      secondary="Purple button in the header bar"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Step 4: Configure settings"
                      secondary="Set start, end, and increment values"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Step 5: Monitor progress"
                      secondary="Watch real-time updates as each task count completes"
                    />
                  </ListItem>
                </List>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Example Configuration
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                  <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace' }}>
                    <strong>Start:</strong> 10<br />
                    <strong>End:</strong> 100<br />
                    <strong>Increment:</strong> 10<br />
                    <strong>Result:</strong> Runs for 10, 20, 30, 40, 50, 60, 70, 80, 90, 100 tasks
                  </Typography>
                </Paper>
              </Box>

              <Paper sx={{ p: 2, backgroundColor: alpha(theme.palette.success.main, 0.1), mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  ✨ Real-Time Updates
                </Typography>
                <Typography variant="body2">
                  During Complete Run, the UI updates automatically after each task count completes. You can watch results, charts, and analysis update in real-time!
                </Typography>
              </Paper>
            </TabPanel>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HelpDialog;

